import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useCrudForm } from "../common/crudForm/useCrudForm";
import { CrudFormLayout, Field } from "../common/crudForm/crudFormLayout";
import { useAuth } from "../../AuthContext";
import { getPersona, savePersona, personasPromiseOptions } from "../../services/personasService";
import { getCondicionesIvaSelect } from "../../services/condicionesIvaService";
import { tiposDocumentos, tiposTelefonos, tiposCargos } from "../utils/utils";
import { getConfig } from "../../config/config.js";

import Avatar from "react-avatar";
import AvatarEdit from "react-avatar-edit";
import ReactTabHotkeyReplacer from "react-tab-hotkey-replacer/dist/ReactTabHotkeyReplacer";
import moment from "moment";
import BootstrapSwitchButton from "bootstrap-switch-button-react";
import ChildArray from "../common/childArray";
import FileBrowser from "../fileBrowser/fileBrowser";

import Joi from "joi";
import { replaceSeparators } from "../utils/replaceSeparators.js";

export default function PersonasForm(props) {
  const history = useHistory();
  const { selectedModule } = useAuth();
  const [condicionesIva, setCondicionesIva] = useState([]);
  const config = getConfig();

  const serviceFolder = "personas";
  const filesUrl = config.filesUrl + "/" + serviceFolder + "/";

  const optionsTratamiento = [
    { label: "Sr.", value: "Sr." },
    { label: "Sra.", value: "Sra." },
    { label: "Srta.", value: "Srta." },
    { label: "Otro", value: "" },
  ];

  const onCancel = props.onCancel;
  const title = "Persona";
  const subTitle = "Datos Personales";

  const initialData = {
    tratamiento: "Sr.",
    personaJuridica: false,
    representantes: [],
    socios: [],
    avatar: "",
    apellido: "",
    nombre: "",
    cuit: "",
    condicionIva: "",
    tipoDocumento: "",
    documento: "",
    email: "",
    domicilio: "",
    localidad: "",
    provincia: "",
    pais: "",
    telefonos: [],
    estadoCivil: "",
    fechaNacimiento: "",
    nombrePadre: "",
    nombreMadre: "",
    notas: "",
    archivos: [],
    manejaCuenta: false,
  };

  const breadcrumbs = [
    {
      title: "Inicio",
      link: "/main",
    },
    {
      title: "Personas",
      link: "/personas",
    },
    {
      title: "Detalles",
    },
  ];

  const telefonosFormat = [
    {
      label: "Tipo",
      width: "20%",
      column: "tipo",
      type: "combo",
      options: tiposTelefonos,
    },
    { label: "Nº", width: "15%", column: "numero", type: "phone", options: [] },
  ];

  const representantesFormat = [
    {
      label: "Cargo",
      width: "20%",
      column: "cargo",
      type: "combo",
      options: tiposCargos,
    },
    {
      label: "Razón Social",
      width: "15%",
      column: "persona",
      displayValue: "item.persona.apellido+(item.persona.nombre?', '+item.persona.nombre:'')",
      type: "asyncCreatable",
      options: [],
      promiseOptions: personasPromiseOptions,
      getChild: getPersona,
    },
  ];

  const handleNewSocio = () => {
    window.open("/personas/nuevo_", "_blank");
  };

  const sociosFormat = [
    {
      label: "Razón Social",
      width: "15%",
      column: "persona",
      displayValue: "item.persona.apellido+(item.persona.nombre?', '+item.persona.nombre:'')",
      type: "asyncCreatable",
      options: [],
      promiseOptions: personasPromiseOptions,
      getChild: getPersona,
    },
    {
      label: "Participación",
      width: "10%",
      column: "participacion",
      type: "number",
      suffix: "%",
    },
  ];

  const telefono = {
    tipo: tiposTelefonos[0].value,
    numero: "",
  };

  const socio = {
    persona: null,
    particiopacion: 0,
  };

  const representante = {
    cargo: tiposCargos[0].value,
    persona: null,
  };

  const schema = Joi.object({
    _id: Joi.string(),
    apellido: Joi.string().label("Apellido").required().messages({
      "string.empty": "Debe ingresar un apellido/razón social",
    }),
    condicionIva: Joi.any()
      .required()
      .custom((value, helpers) => {
        if (!value || !value.label || !value.value) {
          return helpers.error("any.invalid");
        }
        return value;
      })
      .label("Condición de IVA")
      .messages({
        "any.required": "Debe ingresar condición de IVA", // undefined
        "any.invalid": "Debe ingresar condición de IVA", // null / empty props
      }),
  });

  const purifyInitialData = async (data) => {
    const condiciones = await getCondicionesIvaSelect();
    setCondicionesIva(condiciones);
    return {
      ...data,
      condicionIva: condiciones.find((item) => item.label === "Consumidor Final") ?? null,
    };
  };

  const transformBeforeSave = (data) => {
    data.socios.map((socio) => {
      socio.particiopacion = replaceSeparators(socio.participación);
    });
    if (data.tipoDocumento && data.tipoDocumento.value) data.tipoDocumento = data.tipoDocumento.value;
    else data.tipoDocumento = "";
    if (data.condicionIva && typeof data.condicionIva === "object" && data.condicionIva.value)
      data.condicionIva = data.condicionIva.value;
    return data;
  };

  const transformAfterFetch = (persona, condiciones) => {
    const condicion = condiciones.find((item) => item.value === persona.condicionIva?._id);
    return {
      ...persona,
      condicionIva: condicion ?? null,
      fechaNacimiento: persona.fechaNacimiento ? moment(persona.fechaNacimiento).format("DD/MM/YYYY") : "",
    };
  };

  const transformAfterSave = (persona) => {
    const condicion = condicionesIva.find((item) => item.value === persona.condicionIva);
    return {
      ...persona,
      condicionIva: condicion ?? null,
      fechaNacimiento: persona.fechaNacimiento ? moment(persona.fechaNacimiento).format("DD/MM/YYYY") : "",
    };
  };

  const hookFetchData = async (id) => {
    try {
      if (id) {
        const [condiciones, record] = await Promise.all([getCondicionesIvaSelect(), getPersona(id)]);
        setCondicionesIva(condiciones); // for the <select>
        return transformAfterFetch(record, condiciones); // for the transform
      } else {
        const [condiciones] = await Promise.all([getCondicionesIvaSelect()]);
        setCondicionesIva(condiciones); // for the <select>
        return null;
      }
    } catch {
      history.replace("/noconnection");
    }
  };

  const onSave = async (data) => {
    // try {
    const savedRecord = await savePersona(data);
    return savedRecord;
    // } catch (error) {
    //   throw error; // Rethrow the error to be handled by the caller
    // }
  };

  const crudForm = useCrudForm({
    defaultPath: "/personas",
    initialData,
    // hookWillMount,
    purifyInitialData,
    transformBeforeSave,
    transformAfterSave,
    twoStepSave: true,
    onSave,
    // onSuccess,
    // Local (client-side) validation still goes through the extension point.
    schema: schema,
    onLoading: props.onLoading,
    onDoneLoading: props.onDoneLoading,
    hookFetchData,

    // (d) => {
    //   const errs = {};
    //   if (!d.make?.trim()) errs.make = "Make is required.";
    //   if (!d.model?.trim()) errs.model = "Model is required.";
    //   return errs;
    // },
    // Normalize data right before it's sent.
    // transformBeforeSave: (d) => ({
    //   ...d,
    //   vin: d.vin?.toUpperCase(),
    //   registeredAt: new Date().toISOString(),
    // }),
    liveValidate: true,
  });

  const handleChangeTelefonos = (array) => {
    // const { data } = this.state;
    crudForm.data.telefonos = array;
    crudForm.setData({ ...crudForm.data });
  };

  const handleChangeRepresentantes = (array) => {
    // const { data } = this.state;
    crudForm.data.representantes = array;
    crudForm.setData({ ...crudForm.data });
  };

  const handleChangeSocios = (array) => {
    // const { data } = this.state;
    crudForm.data.socios = array;
    crudForm.setData({ ...crudForm.data });
  };
  // --- Overridden submit handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    // // 1. Custom pre-processing the base hook doesn't know about.
    // if (crudForm.data.year && Number(crudForm.data.year) > new Date().getFullYear() + 1) {
    //   crudForm.setErrors({ year: "Year can't be in the future." });
    //   return;
    // }
    // const firstSave = crudForm.data._id ? false : true;
    // 2. Delegate to the base implementation (the "super" call).
    const ok = await crudForm.submit();

    // 3. Custom post-processing, only if the save succeeded.
    // if (ok) {
    //   if (firstSave) {
    //     const renewData = transFormAfterSave(crudForm.data, condicionesIva);
    //     console.log("renuredata");
    //     console.log(renewData);
    //     crudForm.setData(renewData);
    //   }
    // console.log("[analytics] car_saved", crudForm.data.make, crudForm.data.model);
    // alert("Car saved!");
    // }
  };

  return (
    crudForm.data && (
      <CrudFormLayout
        title={title}
        crudForm={crudForm}
        breadcrumbs={breadcrumbs}
        onSubmit={handleSubmit} // note: our override, not crud.handleSubmit
        onCancel={onCancel}
        submitLabel="Guardar"
      >
        <div className="row ">
          <div className="col-2 text-center">
            <div className="avatarContainer">
              <Avatar
                className="avatar"
                name={crudForm.data.nombre + " " + crudForm.data.apellido}
                size="150"
                round="100%"
                src={crudForm.data.avatar}
              />
              {crudForm.data._id && crudForm.action != "C" && (
                <div className="camera" data-toggle="modal" data-target="#modal-default">
                  <i className="fas fa-camera fa-4x "></i>
                </div>
              )}
            </div>
          </div>
          <div className="col-10">
            <ReactTabHotkeyReplacer>
              <div className="row mb-2">
                <div className="col-3">
                  {!crudForm.data.personaJuridica && (
                    <Field crudForm={crudForm} name="tratamiento" type="radio" options={optionsTratamiento} />
                  )}
                </div>

                <div className="col-3">
                  {selectedModule === "obras" && (
                    <Field crudForm={crudForm} name="manejaCuenta" label="Tiene Cuenta" type="toggle" />
                  )}
                </div>
                <div className="col-3"></div>
                {crudForm.action === "C" && (
                  <div className="col-3">
                    <BootstrapSwitchButton
                      checked={crudForm.data.personaJuridica}
                      disabled
                      style="w-100 mb-2"
                      onstyle="dark"
                      offstyle="secondary"
                      onlabel="Persona Jurídica"
                      offlabel="Persona Física"
                      // onChange={(value) => this.handleChangeSwitch(value)}
                    />
                  </div>
                )}
                {crudForm.action !== "C" && (
                  <div className="col-3">
                    <BootstrapSwitchButton
                      checked={crudForm.data.personaJuridica}
                      style="w-100 mb-2"
                      onstyle="dark"
                      offstyle="secondary"
                      onlabel="Persona Jurídica"
                      offlabel="Persona Física"
                      onChange={(value) => {
                        crudForm.data.personaJuridica = value;
                        if (value) crudForm.data.tratamiento = "";
                        else crudForm.data.tratamiento = "Sr.";
                        crudForm.setData({ ...crudForm.data });
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="row">
                {crudForm.data.personaJuridica && (
                  <Field crudForm={crudForm} label="Razón Social" name="apellido" float columns="12" autoFocus />
                )}
                {!crudForm.data.personaJuridica && (
                  <>
                    <Field crudForm={crudForm} label="Apellido" name="apellido" float columns="6" autoFocus />
                    <Field crudForm={crudForm} label="Nombre" name="nombre" float columns="6" />
                  </>
                )}
              </div>
              {crudForm.data._id && (
                <>
                  <div className="row">
                    <div className="col-5">
                      <Field crudForm={crudForm} label="Domicilio" name="domicilio" float />
                    </div>
                    <div className="col-7">
                      <div className="row">
                        <Field crudForm={crudForm} label="Localidad" name="localidad" float columns="4" />
                        <Field crudForm={crudForm} label="Provincia" name="provincia" float columns="4" />
                        <Field crudForm={crudForm} label="País" name="pais" float columns="4" />
                      </div>
                    </div>
                  </div>
                  {crudForm.data.personaJuridica && (
                    <div className="row ">
                      <Field
                        crudForm={crudForm}
                        combo
                        label="Condición de IVA"
                        name="condicionIva"
                        float
                        columns="4"
                        options={condicionesIva}
                      />
                      <Field crudForm={crudForm} cuit label="CUIT/CUIL" name="cuit" float columns="4" />
                      <Field crudForm={crudForm} email label="e-mail" name="email" float columns="4" />
                    </div>
                  )}
                  <div className="row mb-1">
                    {
                      <ChildArray
                        data={crudForm.data.telefonos}
                        blankItem={telefono}
                        editable={crudForm.action === "C" ? false : true}
                        format={telefonosFormat}
                        label="Telefono(s)"
                        onChange={handleChangeTelefonos}
                        onIsEditing={() => {}}
                      />
                    }
                  </div>
                  <div className="row">
                    {crudForm.data.personaJuridica && (
                      <>
                        <ChildArray
                          data={crudForm.data.representantes}
                          blankItem={representante}
                          editable={crudForm.action === "C" ? false : true}
                          format={representantesFormat}
                          label="Representante(s)"
                          onChange={handleChangeRepresentantes}
                          onIsEditing={() => {}}
                        />
                        <ChildArray
                          data={crudForm.data.socios}
                          blankItem={socio}
                          editable={crudForm.action === "C" ? false : true}
                          format={sociosFormat}
                          label="Socio(s)"
                          onChange={handleChangeSocios}
                          onCreateOption={handleNewSocio}
                          onIsEditing={() => {}}
                        />
                      </>
                    )}
                    {!crudForm.data.personaJuridica && (
                      <>
                        <Field
                          combo
                          crudForm={crudForm}
                          label="Tipo Documento"
                          name="tipoDocumento"
                          float
                          columns="2"
                          options={tiposDocumentos}
                        />
                        <Field crudForm={crudForm} dni label="Documento" name="documento" float columns="4" />
                        <Field crudForm={crudForm} label="e-mail" name="email" float columns="6" />
                      </>
                    )}
                  </div>
                  {!crudForm.data.personaJuridica && (
                    <div className="row">
                      <Field
                        crudForm={crudForm}
                        combo
                        label="Condición de IVA"
                        name="condicionIva"
                        float
                        columns="4"
                        options={condicionesIva}
                      />

                      <Field crudForm={crudForm} cuit label="CUIT/CUIL" name="cuit" float columns="6" />
                    </div>
                  )}
                  <div className="row ">
                    <Field crudForm={crudForm} textarea label="Notas" name="notas" float columns="6" rows="10" />
                    <div className="col-6">
                      <FileBrowser
                        status={crudForm.action}
                        recordId={crudForm.data._id}
                        serviceFolder={serviceFolder}
                        filesUrl={filesUrl}
                        canCreateFolder={true}
                        // archivos={archivos}
                        // onFileDelete={(index, subfolder) => this.handleFileDelete(index, subfolder)}
                        // onFileUpload={(files, subfolder) => this.handleFileUpload(files, subfolder)}
                        // onCreateFolder={this.handleCreateFolder}
                        // onChangeFolder={(url) => this.handleFolderChange(url)}
                        // onRename={(url, original, newName) => this.handleRename(url, original, newName)}
                      />
                    </div>
                  </div>
                </>
              )}
            </ReactTabHotkeyReplacer>
          </div>
        </div>
      </CrudFormLayout>
    )
  );
}
